import React, { PropsWithChildren, ReactNode } from 'react';
import { SharedIcon } from '../../SharedIcon/SharedIcon';
import { useCollapse } from '../../../hooks/useCollapse';
import classNames from 'classnames';

interface CollapsibleCardProps {
  sectionKey: string;
  headerLeft: ReactNode;
  headerRight?: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  onCollapse?: () => void;
  onStateChange?: (isExpanded: boolean) => void;
  defaultCollapsed?: boolean;
  arrowPosition?: 'left' | 'right';
}

export const CollapsibleCard = ({
  sectionKey,
  headerLeft,
  headerRight,
  className,
  headerClassName,
  contentClassName,
  onCollapse,
  onStateChange,
  defaultCollapsed,
  arrowPosition = 'right',
  children,
}: PropsWithChildren<CollapsibleCardProps>) => {
  const contentId = React.useId();
  const { onToggleCollapse, isExpanded, show, isTransitioning, contentRef } =
    useCollapse(sectionKey, defaultCollapsed);

  // Keep a stable ref so the effect only re-runs when `show` changes,
  // not when the caller passes a new inline callback reference.
  const onStateChangeRef = React.useRef(onStateChange);
  React.useEffect(() => {
    onStateChangeRef.current = onStateChange;
  });

  // Notify parent of state changes
  React.useEffect(() => {
    onStateChangeRef.current?.(show);
  }, [show]);

  const arrow = (
    <span className={!show ? 'collapse-arrow' : 'collapse-arrow collapsed'}>
      <SharedIcon iconName="keyboard_arrow_right" size="sm" />
    </span>
  );

  const handleToggle = () => {
    onToggleCollapse();
    if (show && onCollapse) {
      onCollapse();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleToggle();
  };

  return (
    <div className={className}>
      <div
        className={classNames(
          'fs-6 d-flex justify-content-between align-items-center header',
          headerClassName,
          { collapsed: !show }
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <div className="d-flex align-items-center gap-2">
          {arrowPosition === 'left' && arrow}
          {headerLeft}
        </div>
        <div className="d-flex align-items-center gap-2">
          {headerRight}
          {arrowPosition === 'right' && arrow}
        </div>
      </div>
      <div
        id={contentId}
        ref={contentRef}
        className={classNames(isTransitioning ? 'collapsing' : 'collapse', {
          show: show,
        })}
        aria-hidden={!isExpanded}
      >
        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
};
